<template>
  <div v-loading="isFetchingOrder">
    <!-- Алерти -->
    <section v-if="errorMessage" ref="errorAlert" class="mini-kasko-order__alert mini-kasko-order__alert--error" tabindex="-1">
      {{ errorMessage }}
    </section>
    <section v-if="successMessage" class="mini-kasko-order__alert mini-kasko-order__alert--success">
      {{ successMessage }}
    </section>
    <section v-if="offerValidationAlert" class="mini-kasko-order__alert mini-kasko-order__alert--warning">
      {{ offerValidationAlert }}
    </section>
    <section v-if="cityChangedWarning" class="mini-kasko-order__alert mini-kasko-order__alert--warning">
      {{ cityChangedWarning }}
    </section>

    <!-- Concluded view -->
    <MiniKaskoOrderConcluded
      v-if="localIsConcluded"
      :form="form"
      :policy-number="policyNumber"
      :policy-period="policyPeriod"
      :policy-end-date="policyEndDate"
      :insurant-name="insurantName"
      :vehicle-label="vehicleLabel"
      :selected-car-type-label="selectedCarTypeLabel"
      :customer-address-label="customerAddressLabel"
      :is-processing="isProcessing"
      @go-to-offers="goToOffers"
      @open-contract="openContract"
    />

    <!-- Form editing -->
    <section v-else class="mini-kasko-order__card">
      <div class="mini-kasko-order__grid">
        <div class="mini-kasko-order__group mini-kasko-order__group--wide">
          <h2 class="mini-kasko-order__group-title">Параметри поліса</h2>
          <label class="mini-kasko-order__field">
            <span>Дата початку</span>
            <input ref="dates.startDate" v-model="form.dates.startDate" type="date" :disabled="localIsDeclared"
                   @input="clearFieldBackendError('dates.startDate')">
            <small v-if="getFieldError('dates.startDate')" class="mini-kasko-order__field-error">{{ getFieldError('dates.startDate') }}</small>
          </label>
        </div>
        <MiniKaskoCustomerForm
          ref="customerForm"
          v-model="form.customer"
          :is-declared="localIsDeclared"
          :errors="customerFieldErrors"
          @field-change="clearFieldBackendError(`customer.${$event}`)"
        />
        <MiniKaskoAddressForm
          ref="addressForm"
          v-model="form.customer"
          :is-declared="localIsDeclared"
          :errors="customerAddressFieldErrors"
          @field-change="clearFieldBackendError(`customer.${$event}`)"
        />
        <MiniKaskoCarForm
          ref="carForm"
          v-model="form.car"
          :is-declared="localIsDeclared"
          :errors="carFieldErrors"
          :show-recalculate="!!cityChangedWarning"
          @field-change="clearFieldBackendError(`car.${$event}`)"
          @recalculate="recalculateWithNewCity"
        />
      </div>

      <div v-if="!localIsDeclared" class="mini-kasko-order__actions">
        <button
          v-if="!form.offerId"
          type="button"
          class="mini-kasko-order__action mini-kasko-order__action--secondary"
          :disabled="isProcessing"
          @click="goToOffers"
        >
          До пропозицій
        </button>
        <button
          type="button"
          class="mini-kasko-order__action mini-kasko-order__action--secondary"
          :disabled="isProcessing || !!cityChangedWarning"
          :title="cityChangedWarning || undefined"
          @click="saveDraft"
        >
          {{ localOrderId ? 'Оновити чернетку' : 'Створити чернетку' }}
        </button>
        <button
          type="button"
          class="mini-kasko-order__action mini-kasko-order__action--primary"
          :disabled="isProcessing || !!cityChangedWarning"
          :title="cityChangedWarning || undefined"
          @click="declareOrder"
        >
          {{ isSubmitting ? 'Обробка...' : 'Заявити' }}
        </button>
      </div>

      <div v-else class="mini-kasko-order__actions">
        <button
          type="button"
          class="mini-kasko-order__action mini-kasko-order__action--secondary"
          :disabled="isProcessing"
          @click="openDraft"
        >
          Переглянути чернетку
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment';
import { HttpClient } from '@/shared/lib/http-client';
import { getCarInfoByNumber } from '@/api/car';
import MiniKaskoCustomerForm from './MiniKaskoCustomerForm.vue';
import MiniKaskoAddressForm from './MiniKaskoAddressForm.vue';
import MiniKaskoCarForm from './MiniKaskoCarForm.vue';
import MiniKaskoOrderConcluded from './MiniKaskoOrderConcluded.vue';

const buildDefaultForm = (offerId = '', startDate = '') => ({
  offerId: offerId ? Number(offerId) : '',
  car: {
    vin: '',
    year: '',
    is_taxi: 0,
    reg_num: '',
    mark_name: '',
    model_name: '',
    category: '',
    registration_city_id: null,
    registration_city_name: '',
  },
  dates: {
    startDate,
  },
  customer: {
    code: '',
    surname: '',
    name: '',
    middle_name: '',
    phone: '',
    email: '',
    document_number: '',
    document_issued_by: '',
    document_issued_date: '',
    born_date: '',
    city_id: null,
    city_name: '',
    street: '',
    house: '',
    apartment: '',
  },
});

const readSelectedOffer = () => {
  try {
    const value = sessionStorage.getItem('miniKaskoSelectedOffer');
    return value ? JSON.parse(value) : null;
  } catch (error) {
    return null;
  }
};

export default {
  name: 'MiniKaskoOrderFormSection',
  components: { MiniKaskoCustomerForm, MiniKaskoAddressForm, MiniKaskoCarForm, MiniKaskoOrderConcluded },
  props: {
    orderId: { type: String, default: '' },
    isDeclared: { type: Boolean, default: false },
    isConcluded: { type: Boolean, default: false },
  },
  emits: ['order-updated'],
  data() {
    const selectedOffer = readSelectedOffer();
    const offerId = this.$route.query.offerId || selectedOffer?.offerId || '';
    const startDate = this.resolveStartDate(
      this.$route.query.startDate || this.$route.query.start_date || selectedOffer?.startDate
    );

    return {
      form: buildDefaultForm(offerId, startDate),
      price: null,
      earnings: null,
      earningsPercent: null,
      backendValidationErrors: {},
      isSubmitting: false,
      isFetchingOrder: false,
      errorMessage: '',
      successMessage: '',
      isOfferValid: true,
      isOfferExpired: false,
      offerValidationMessage: '',
      calculationCityId: null,
      calculationCityName: '',
      policyNumber: '',
      contractUrl: '',
      companyId: '',
      isMounted: false,
      isInitializing: true,
      statusId: null,
      localIsDeclared: false,
      localIsConcluded: false,
      localOrderId: '',
    };
  },
  async created() {
    this.localOrderId = this.orderId;
    this.localIsDeclared = this.isDeclared;
    this.localIsConcluded = this.isConcluded;

    this.hydrateSelectedOffer();

    if (this.localOrderId) {
      const fetchSuccess = await this.fetchOrder();
      if (fetchSuccess) {
        this.restoreOrderFormDraft();
      }
    } else {
      await this.prefillCarFromSelectedOffer();
      this.restoreOrderFormDraft();
    }

    this.isInitializing = false;

    if (this.cityChangedWarning) {
      this.$nextTick(() => {
        this.focusField('car.registration_city_id');
      });
    }
  },
  mounted() {
    this.isMounted = true;
  },
  watch: {
    '$route.query.offerId'() {
      this.hydrateSelectedOffer();
    },
    orderId(val) {
      this.localOrderId = val;
    },
    isDeclared(val) {
      this.localIsDeclared = val;
    },
    isConcluded(val) {
      this.localIsConcluded = val;
      if (val) {
        this.fetchOrder();
      }
    },
    cityChangedWarning(newVal) {
      if (newVal && this.isMounted && !this.isInitializing) {
        this.$nextTick(() => {
          this.focusField('car.registration_city_id');
        });
      }
    },
  },
  computed: {
    isProcessing() {
      return this.isSubmitting || this.isFetchingOrder;
    },
    policyPeriod() {
      if (!this.form.dates.startDate) {
        return 'Не вказано';
      }

      const start = moment(this.form.dates.startDate, 'YYYY-MM-DD', true);
      if (!start.isValid()) {
        return 'Не вказано';
      }

      return `${start.format('DD.MM.YYYY')} - ${start.clone().add(1, 'year').format('DD.MM.YYYY')}`;
    },
    policyEndDate() {
      if (!this.form.dates.startDate) {
        return 'Не вказано';
      }

      const start = moment(this.form.dates.startDate, 'YYYY-MM-DD', true);

      if (!start.isValid()) {
        return 'Не вказано';
      }

      return start.clone().add(1, 'year').format('DD.MM.YYYY');
    },
    insurantName() {
      const parts = [
        this.form.customer.surname,
        this.form.customer.name,
        this.form.customer.middle_name,
      ].filter(Boolean);

      return parts.length ? parts.join(' ') : 'Не вказано';
    },
    vehicleLabel() {
      const parts = [
        this.form.car.mark_name,
        this.form.car.model_name,
        this.form.car.reg_num,
      ].filter(Boolean);

      return parts.length ? parts.join(' / ') : 'Не вказано';
    },
    autoCategoryTypes() {
      return this.$store.getters.AUTO_CATEGORY_TYPES || [];
    },
    selectedCarTypeLabel() {
      for (const group of this.autoCategoryTypes) {
        const matchedType = (group.list || []).find((item) => item.id === this.form.car.category);

        if (matchedType) {
          return `${group.category} - ${matchedType.name}`;
        }
      }

      return '';
    },
    customerAddressLabel() {
      const parts = [
        this.form.customer.city_name,
        this.form.customer.street,
        this.form.customer.house,
        this.form.customer.apartment,
      ].filter(Boolean);

      return parts.length ? parts.join(', ') : '';
    },
    customerFieldErrors() {
      return {
        code: this.getFieldError('customer.code'),
        surname: this.getFieldError('customer.surname'),
        name: this.getFieldError('customer.name'),
        middle_name: this.getFieldError('customer.middle_name'),
        phone: this.getFieldError('customer.phone'),
        email: this.getFieldError('customer.email'),
        document_number: this.getFieldError('customer.document_number'),
        document_issued_by: this.getFieldError('customer.document_issued_by'),
        document_issued_date: this.getFieldError('customer.document_issued_date'),
        born_date: this.getFieldError('customer.born_date'),
      };
    },
    customerAddressFieldErrors() {
      return {
        city_id: this.getFieldError('customer.city_id'),
        street: this.getFieldError('customer.street'),
        house: this.getFieldError('customer.house'),
        apartment: this.getFieldError('customer.apartment'),
      };
    },
    carFieldErrors() {
      return {
        reg_num: this.getFieldError('car.reg_num'),
        vin: this.getFieldError('car.vin'),
        year: this.getFieldError('car.year'),
        mark_name: this.getFieldError('car.mark_name'),
        model_name: this.getFieldError('car.model_name'),
        category: this.getFieldError('car.category'),
        registration_city_id: this.getFieldError('car.registration_city_id'),
        is_taxi: this.getFieldError('car.is_taxi'),
      };
    },
    offerValidationAlert() {
      if (this.isOfferValid) {
        return '';
      }

      if (this.isOfferExpired) {
        return 'Пропозиція MiniKasko застаріла. Перейдіть до пропозицій та оберіть актуальний тариф.';
      }

      return this.offerValidationMessage || 'Пропозиція MiniKasko більше не доступна. Перейдіть до пропозицій та оберіть актуальний тариф.';
    },
    cityChangedWarning() {
      if (this.localIsDeclared || this.localIsConcluded) return '';
      if (!this.calculationCityId || !this.form.car.registration_city_id) return '';
      if (Number(this.calculationCityId) !== Number(this.form.car.registration_city_id)) {
        const cityLabel = this.calculationCityName ? `«${this.calculationCityName}»` : 'іншого міста';
        return `Ціна розрахована для ${cityLabel}. Змінене місто реєстрації може вплинути на тариф.`;
      }
      return '';
    },
  },
  methods: {
    normalizeOrderId(value) {
      if (value === undefined || value === null || value === '' || value === 'undefined' || value === 'null') {
        return '';
      }

      return String(value);
    },
    hasValidOrderId(value = this.localOrderId) {
      return Boolean(this.normalizeOrderId(value));
    },
    replaceWithCleanOrderUrl() {
      if (!this.hasValidOrderId()) {
        return;
      }

      this.$router.replace({
        name: 'miniKaskoOrder',
        params: {
          orderId: this.normalizeOrderId(this.localOrderId),
        },
      });
    },
    goToOffers() {
      const query = {};

      if (this.localOrderId && !this.form.offerId) {
        query.copyOrderId = this.normalizeOrderId(this.localOrderId);
      }

      if (this.form.dates.startDate) {
        query.startDate = this.form.dates.startDate;
      }

      const cityId = this.form.car.registration_city_id || this.calculationCityId;
      const cityName = this.form.car.registration_city_name || this.calculationCityName;

      if (cityId) {
        query.city_id = cityId;
      }

      if (cityName) {
        query.city_name = cityName;
      }

      if (this.form.car.reg_num) {
        query.plate = this.form.car.reg_num;
      }

      this.$router.push({
        name: 'miniKaskoOffers',
        query,
      });
    },
    recalculateWithNewCity() {
      try {
        sessionStorage.setItem('miniKaskoOrderFormDraft', JSON.stringify({
          // Мета-поля для валідації при відновленні
          _orderId: this.localOrderId || null,
          _cityId: this.form.car.registration_city_id || null,
          car: this.form.car,
          customer: this.form.customer,
          dates: this.form.dates,
          plate: this.form.car.reg_num || '',
        }));
        // Очищаємо старий офер, щоб при «Назад» у браузері не відновився стейл
        sessionStorage.removeItem('miniKaskoSelectedOffer');
      } catch (e) {
        // ignore
      }

      const query = {};

      // Зберігаємо зв'язок із поточною чернеткою, щоб вона не стала осиротілою
      if (this.localOrderId) {
        query.copyOrderId = this.normalizeOrderId(this.localOrderId);
      }

      if (this.form.car.registration_city_id) {
        query.city_id = this.form.car.registration_city_id;
      }
      if (this.form.car.registration_city_name) {
        query.city_name = this.form.car.registration_city_name;
      }
      if (this.form.car.reg_num) {
        query.plate = this.form.car.reg_num;
      }
      if (this.form.dates.startDate) {
        query.startDate = this.form.dates.startDate;
      }

      this.$router.push({
        name: 'miniKaskoOffers',
        query,
      });
    },
    restoreOrderFormDraft() {
      try {
        const raw = sessionStorage.getItem('miniKaskoOrderFormDraft');
        if (!raw) return;

        const draft = JSON.parse(raw);

        if (!draft) return;

        // Захист від крос-замовного забруднення: чернетка прив'язана до конкретного orderId.
        // Якщо обидва є й не збігаються — ігноруємо чернетку (вона від іншого замовлення).
        const draftOrderId = draft._orderId ? String(draft._orderId) : null;
        const currentOrderId = this.localOrderId ? String(this.localOrderId) : null;
        if (draftOrderId && currentOrderId && draftOrderId !== currentOrderId) {
          return;
        }

        const defaults = buildDefaultForm();

        // Пріоритет міста: нове місто з офера (calculationCityId) →
        //   місто збережене у чернетці (_cityId — завжди є при recalculate) →
        //   поточне form.car (завантажено з БД або prefill).
        const newCityId = this.calculationCityId || draft._cityId || this.form.car.registration_city_id;
        const newCityName = this.calculationCityName || this.form.car.registration_city_name;

        if (draft.car) {
          // Деструктуруємо, щоб поля міста з чернетки ніколи не потрапляли в spread
          // eslint-disable-next-line no-unused-vars
          const { registration_city_id: _cid, registration_city_name: _cname, ...draftCarWithoutCity } = draft.car;
          this.form.car = {
            ...defaults.car,
            ...draftCarWithoutCity,
            registration_city_id: newCityId,
            registration_city_name: newCityName,
          };
        }

        if (draft.customer) {
          this.form.customer = {
            ...defaults.customer,
            ...draft.customer,
          };
        }

        if (draft.dates?.startDate) {
          this.form.dates.startDate = draft.dates.startDate;
        }

        // Видаляємо тільки після успішного застосування — щоб не втратити дані при помилці
        sessionStorage.removeItem('miniKaskoOrderFormDraft');
      } catch (e) {
        // ignore
      }
    },
    redirectToOffersOnNotFound(error) {
      if (error?.response?.status !== 404) {
        return false;
      }

      this.$router.push({
        name: 'miniKaskoOffers',
      });

      return true;
    },
    clearBackendValidationErrors() {
      this.backendValidationErrors = {};
    },
    getFieldError(field) {
      const value = this.backendValidationErrors[field];

      return Array.isArray(value) ? value[0] : '';
    },
    clearFieldBackendError(field) {
      if (!this.backendValidationErrors[field]) {
        return;
      }

      this.backendValidationErrors = {
        ...this.backendValidationErrors,
        [field]: null,
      };
    },
    focusErrorAlert() {
      this.$nextTick(() => {
        const alert = this.$refs.errorAlert;

        if (!alert) {
          return;
        }

        if (typeof alert.scrollIntoView === 'function') {
          alert.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }

        if (typeof alert.focus === 'function') {
          try {
            alert.focus({
              preventScroll: true,
            });
          } catch (focusError) {
            alert.focus();
          }
        }
      });
    },
    focusField(field) {
      this.$nextTick(() => {
        if (field.startsWith('customer.')) {
          const customerField = field.replace('customer.', '');
          const isAddressField = ['city_id', 'street', 'house', 'apartment'].includes(customerField);
          const formRef = isAddressField ? this.$refs.addressForm : this.$refs.customerForm;

          if (formRef && typeof formRef.focus === 'function' && formRef.focus(customerField)) {
            return;
          }
        }

        if (field.startsWith('car.')) {
          const carField = field.replace('car.', '');
          const carFormRef = this.$refs.carForm;

          if (carFormRef && typeof carFormRef.focus === 'function' && carFormRef.focus(carField)) {
            return;
          }
        }

        const ref = this.$refs[field];
        const element = Array.isArray(ref) ? ref[0] : ref;
        const scrollTarget = element && element.$el ? element.$el : element;

        if (!element && !scrollTarget) {
          return;
        }

        if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
          scrollTarget.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }

        if (typeof element.focus === 'function') {
          try {
            element.focus({
              preventScroll: true,
            });
          } catch (focusError) {
            element.focus();
          }
          return;
        }

        if (scrollTarget && typeof scrollTarget.focus === 'function') {
          try {
            scrollTarget.focus({
              preventScroll: true,
            });
          } catch (focusError) {
            scrollTarget.focus();
          }
        }
      });
    },
    focusFirstBackendValidationError() {
      const fieldOrder = [
        'dates.startDate',
        'customer.code',
        'customer.surname',
        'customer.name',
        'customer.middle_name',
        'customer.phone',
        'customer.email',
        'customer.document_number',
        'customer.born_date',
        'customer.city_id',
        'customer.street',
        'customer.house',
        'customer.apartment',
        'car.reg_num',
        'car.vin',
        'car.year',
        'car.mark_name',
        'car.model_name',
        'car.category',
        'car.registration_city_id',
        'car.is_taxi',
      ];
      const firstFieldWithError = fieldOrder.find((field) => this.getFieldError(field));

      if (firstFieldWithError) {
        this.focusField(firstFieldWithError);
        return true;
      }

      return false;
    },
    applyBackendValidationErrors(error) {
      const errors = error?.response?.data?.data || error?.response?.data?.errors;

      if (!errors || typeof errors !== 'object') {
        this.backendValidationErrors = {};
        return;
      }

      this.backendValidationErrors = errors;
    },
    resolveRequestErrorMessage(error, fallbackMessage) {
      const status = error?.response?.data?.status;
      const message = error?.response?.data?.message;

      if (status === 'validation' && message === 'The given data was invalid.') {
        return '';
      }

      return message || fallbackMessage;
    },
    extractFilenameFromDisposition(disposition) {
      if (!disposition) {
        return '';
      }

      const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);

      if (utfMatch && utfMatch[1]) {
        return decodeURIComponent(utfMatch[1].replace(/["']/g, ''));
      }

      const basicMatch = disposition.match(/filename="?([^"]+)"?/i);

      return basicMatch && basicMatch[1] ? basicMatch[1] : '';
    },
    triggerFileDownload(blob, filename) {
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    },
    async resolveBlobErrorMessage(error, fallbackMessage) {
      const responseData = error?.response?.data;

      if (responseData instanceof Blob) {
        try {
          const parsed = JSON.parse(await responseData.text());

          return parsed?.message || fallbackMessage;
        } catch (blobError) {
          return fallbackMessage;
        }
      }

      return error?.response?.data?.message || fallbackMessage;
    },
    async openDocument(url, fallbackFilename, fallbackMessage) {
      if (!this.localOrderId) {
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const response = await HttpClient.get(
          url,
          {},
          { responseType: 'blob' },
        );
        const filename = this.extractFilenameFromDisposition(response.headers['content-disposition']) || fallbackFilename;
        const contentType = response.headers['content-type'] || 'application/pdf';
        const blob = new Blob([response.data], { type: contentType });

        this.triggerFileDownload(blob, filename);
      } catch (error) {
        this.errorMessage = await this.resolveBlobErrorMessage(error, fallbackMessage);
      } finally {
        this.isSubmitting = false;
      }
    },
    async openDraft() {
      await this.openDocument(
        `/insurance/mini-kasko/orders/${this.localOrderId}/download-draft`,
        `draft-${this.localOrderId}.pdf`,
        'Не вдалося отримати чернетку.',
      );
    },
    async openContract() {
      await this.openDocument(
        `/insurance/mini-kasko/orders/${this.localOrderId}/download?t=${Date.now()}`,
        `policy-${this.localOrderId}.pdf`,
        'Не вдалося отримати поліс.',
      );
    },
    async prefillCarFromSelectedOffer() {
      const selectedOffer = readSelectedOffer();
      const plate = selectedOffer?.plate;
      const cityId = selectedOffer?.registration_city_id || null;
      const cityName = selectedOffer?.registration_city_name || '';

      if (!plate && !cityId) return;

      if (cityId) {
        this.calculationCityId = cityId;
        this.calculationCityName = cityName;
        this.form.car = {
          ...this.form.car,
          registration_city_id: cityId,
          registration_city_name: cityName,
        };
      }

      if (!plate) return;

      try {
        const { data } = await getCarInfoByNumber(plate);
        const result = data?.data;

        if (!result) return;

        const city = result.city && typeof result.city === 'object'
          ? {
            id: result.city.id || result.city.city_id || null,
            name: result.city.name_full_name_ua || result.city.name_ua || result.city.name || '',
          }
          : {};

        const resolvedCityId = cityId || city.id || this.form.car.registration_city_id;
        const resolvedCityName = cityId ? cityName : (city.name || this.form.car.registration_city_name);

        if (resolvedCityId) {
          this.calculationCityId = resolvedCityId;
          this.calculationCityName = resolvedCityName;
        }

        this.form.car = {
          ...this.form.car,
          reg_num: plate,
          mark_name: result.mark || this.form.car.mark_name,
          model_name: result.model || this.form.car.model_name,
          year: result.year ? String(result.year) : this.form.car.year,
          vin: result.vin || this.form.car.vin,
          category: result.type || this.form.car.category,
          registration_city_id: resolvedCityId,
          registration_city_name: resolvedCityName,
        };
      } catch {
        // не блокуємо форму якщо lookup не спрацював
      }
    },
    hydrateSelectedOffer() {
      const selectedOffer = readSelectedOffer();
      const routeOfferId = this.$route.query.offerId || this.form.offerId || selectedOffer?.offerId;
      const startDate = this.resolveRouteStartDate() || this.resolveDateValue(selectedOffer?.startDate);

      if (!selectedOffer || Number(selectedOffer.offerId) !== Number(routeOfferId)) {
        return;
      }

      this.form.offerId = Number(selectedOffer.offerId);
      if (startDate) {
        this.form.dates.startDate = startDate;
      }
      this.isOfferValid = true;
      this.isOfferExpired = false;
      this.offerValidationMessage = '';
      this.price = Number(selectedOffer.price);
      this.earnings = selectedOffer.earnings !== undefined && selectedOffer.earnings !== null
        ? Number(selectedOffer.earnings)
        : null;
      this.earningsPercent = selectedOffer.earningsPercent !== undefined && selectedOffer.earningsPercent !== null
        ? Number(selectedOffer.earningsPercent)
        : null;
      if (selectedOffer.registration_city_id) {
        this.calculationCityId = selectedOffer.registration_city_id;
        this.calculationCityName = selectedOffer.registration_city_name || '';
      }
    },
    resolveStartDate(value) {
      return this.resolveDateValue(value) || moment().add(1, 'day').format('YYYY-MM-DD');
    },
    resolveDateValue(value) {
      if (!value || !moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY'], true).isValid()) {
        return '';
      }

      return moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY']).format('YYYY-MM-DD');
    },
    resolveRouteStartDate() {
      const routeStartDate = this.$route.query.startDate || this.$route.query.start_date;

      return this.resolveDateValue(routeStartDate);
    },
    buildPayload() {
      const payload = {
        car: {
          vin: this.form.car.vin,
          year: Number(this.form.car.year),
          is_taxi: Number(this.form.car.is_taxi),
          reg_num: this.form.car.reg_num,
          mark_name: this.form.car.mark_name,
          model_name: this.form.car.model_name,
          category: String(this.form.car.category),
          registration_city_id: this.form.car.registration_city_id ? Number(this.form.car.registration_city_id) : null,
          registration_city_name: this.form.car.registration_city_name || null,
        },
        dates: {
          startDate: this.form.dates.startDate,
        },
        customer: {
          code: this.form.customer.code,
          surname: this.form.customer.surname,
          name: this.form.customer.name,
          middle_name: this.form.customer.middle_name || null,
          document_number: this.form.customer.document_number,
          document_issued_by: this.form.customer.document_issued_by,
          document_issued_date: this.form.customer.document_issued_date,
          phone: this.form.customer.phone,
          email: this.form.customer.email || null,
          city_id: this.form.customer.city_id ? Number(this.form.customer.city_id) : null,
          city_name: this.form.customer.city_name || null,
          street: this.form.customer.street,
          house: this.form.customer.house,
          apartment: this.form.customer.apartment || null,
          born_date: this.form.customer.born_date,
        },
      };

      if (this.form.offerId) {
        payload.offerId = Number(this.form.offerId);
      }

      return payload;
    },
    applyOrderData(order) {
      const statusId = Number(order.statusId ?? this.statusId);

      this.price = order.offer?.price ?? order.price ?? this.price;
      this.earnings = order.offer?.earnings !== undefined && order.offer?.earnings !== null
        ? Number(order.offer.earnings)
        : this.earnings;
      this.earningsPercent = order.offer?.earningsPercent !== undefined && order.offer?.earningsPercent !== null
        ? Number(order.offer.earningsPercent)
        : this.earningsPercent;
      this.statusId = Number.isNaN(statusId) ? this.statusId : statusId;
      this.localIsDeclared = typeof order.isDeclared === 'boolean'
        ? order.isDeclared
        : statusId === 5;
      this.localIsConcluded = statusId === 10;
      this.policyNumber = order.draft?.policyNumber || this.policyNumber;
      this.contractUrl = order.contractUrl || '';
      this.companyId = order.companyInfo?.companyId || this.companyId;
      this.isOfferValid = order.isOfferValid !== undefined ? Boolean(order.isOfferValid) : true;
      this.isOfferExpired = Boolean(order.isOfferExpired);
      this.offerValidationMessage = order.offerValidationMessage || '';
      this.form = {
        offerId: order.offer?.id || '',
        car: {
          vin: order.insuranceObject?.vin || '',
          year: order.insuranceObject?.year || '',
          is_taxi: Number(order.insuranceObject?.is_taxi || 0),
          reg_num: order.insuranceObject?.reg_num || '',
          mark_name: order.insuranceObject?.mark_name || '',
          model_name: order.insuranceObject?.model_name || '',
          category: order.insuranceObject?.category || '',
          registration_city_id: order.insuranceObject?.registration_city_id || null,
          registration_city_name: order.insuranceObject?.registration_city_name || '',
        },
        dates: {
          startDate: order.startedAt
            ? moment(order.startedAt, 'DD.MM.YYYY').format('YYYY-MM-DD')
            : this.form.dates.startDate,
        },
        customer: {
          code: order.insurant?.code || '',
          surname: order.insurant?.surname || '',
          name: order.insurant?.name || '',
          middle_name: order.insurant?.middle_name || '',
          document_number: order.insurant?.document_number || '',
          document_issued_by: order.insurant?.document_issued_by || '',
          document_issued_date: order.insurant?.document_issued_date || '',
          phone: order.insurant?.phone || '',
          email: order.insurant?.email || '',
          city_id: order.insurant?.city_id || null,
          city_name: order.insurant?.city_name || '',
          street: order.insurant?.street || '',
          house: order.insurant?.house || '',
          apartment: order.insurant?.apartment || '',
          born_date: order.insurant?.born_date || '',
        },
      };
      this.hydrateSelectedOffer();

      const resolvedOrderId = order.id || this.localOrderId;
      if (resolvedOrderId) {
        this.localOrderId = resolvedOrderId;
      }

      this.$emit('order-updated', {
        orderId: resolvedOrderId,
        isDeclared: this.localIsDeclared,
        isConcluded: this.localIsConcluded,
        price: this.price,
        earnings: this.earnings,
        earningsPercent: this.earningsPercent,
        policyNumber: this.policyNumber,
        statusId: Number.isNaN(statusId) ? this.statusId : statusId,
        insurantName: this.insurantName,
        customerCode: this.form.customer.code,
        companyId: this.companyId,
        otpPreloadedState: {
          isPaid: Boolean(order.isPaid),
          hasUploadedLegalReceipt: Boolean(order.hasUploadedLegalReceipt),
          canConfirmByPayment: Boolean(order.canConfirmByPayment || order.isPaid || order.hasUploadedLegalReceipt),
          acceptedPaymentType: order.acceptedPaymentType || '',
          payments: Array.isArray(order.payments) ? order.payments : [],
          otpHistory: order.otpHistory || null,
        },
      });
    },
    async fetchOrder() {
      if (!this.hasValidOrderId()) {
        return false;
      }

      this.errorMessage = '';
      this.isFetchingOrder = true;

      try {
        const response = await HttpClient.get(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.localOrderId)}`);
        this.applyOrderData(response.data.data);
        return true;
      } catch (error) {
        if (this.redirectToOffersOnNotFound(error)) {
          // redirect відбувся — не продовжуємо ніяку логіку після fetchOrder
          return false;
        }

        this.errorMessage = error?.response?.data?.message || 'Не вдалося завантажити чернетку.';
        this.focusErrorAlert();
        return false;
      } finally {
        this.isFetchingOrder = false;
      }
    },
    async saveDraft() {
      this.isSubmitting = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.clearBackendValidationErrors();

      try {
        if (!await this.$refs.carForm.validateRegNum()) {
          this.errorMessage = 'Перевірте держномер автомобіля.';
          this.focusField('car.reg_num');
          return;
        }

        if (!this.$refs.customerForm.validateCustomerCode()) {
          this.errorMessage = 'Перевірте ІПН страхувальника.';
          this.focusField('customer.code');
          return;
        }

        const payload = this.buildPayload();

        if (this.localOrderId) {
          await HttpClient.put(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.localOrderId)}`, payload);
          this.successMessage = 'Чернетку оновлено.';
          await this.fetchOrder();
        } else {
          const response = await HttpClient.post('/insurance/mini-kasko/orders/init', payload);
          const newOrderId = this.normalizeOrderId(response.data?.id);

          if (!this.hasValidOrderId(newOrderId)) {
            this.errorMessage = 'Не вдалося отримати ідентифікатор чернетки.';
            this.focusErrorAlert();
            return;
          }

          this.localOrderId = newOrderId;
          this.$emit('order-updated', { orderId: newOrderId });
          this.successMessage = 'Чернетку створено.';
          this.$router.replace({
            name: 'miniKaskoOrder',
            params: { orderId: newOrderId },
          });
          await this.fetchOrder();
        }
      } catch (error) {
        this.applyBackendValidationErrors(error);
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося зберегти чернетку.');

        if (!this.focusFirstBackendValidationError()) {
          this.focusErrorAlert();
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    async declareOrder() {
      this.isSubmitting = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.clearBackendValidationErrors();

      try {
        if (!await this.$refs.carForm.validateRegNum()) {
          this.errorMessage = 'Перевірте держномер автомобіля.';
          this.focusField('car.reg_num');
          return;
        }

        if (!this.$refs.customerForm.validateCustomerCode()) {
          this.errorMessage = 'Перевірте ІПН страхувальника.';
          this.focusField('customer.code');
          return;
        }

        if (!this.localOrderId) {
          const response = await HttpClient.post('/insurance/mini-kasko/orders', this.buildPayload());

          const newOrderId = this.normalizeOrderId(response.data?.id);
          if (!this.hasValidOrderId(newOrderId)) {
            this.errorMessage = 'Не вдалося отримати ідентифікатор замовлення.';
            this.focusErrorAlert();
            return;
          }

          this.localOrderId = newOrderId;
          this.localIsDeclared = typeof response.data.isDeclared === 'boolean' ? response.data.isDeclared : true;
          this.statusId = Number(response.data.statusId ?? this.statusId);
          this.policyNumber = response.data.policyNumber || this.policyNumber;
          this.$emit('order-updated', { orderId: newOrderId });
          this.$router.replace({
            name: 'miniKaskoOrder',
            params: { orderId: newOrderId },
          });
          this.successMessage = response.data.policyNumber
            ? `Поліс заявлено. Номер: ${response.data.policyNumber}`
            : 'Поліс заявлено.';
          await this.fetchOrder();
          return;
        }

        if (this.localOrderId) {
          await HttpClient.put(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.localOrderId)}`, this.buildPayload());
        }

        const response = await HttpClient.post(`/insurance/mini-kasko/orders/${this.normalizeOrderId(this.localOrderId)}/declare`);
        const newOrderId = this.normalizeOrderId(response.data?.id);
        if (!this.hasValidOrderId(newOrderId)) {
          this.errorMessage = 'Не вдалося отримати ідентифікатор замовлення.';
          this.focusErrorAlert();
          return;
        }

        this.localOrderId = newOrderId;
        this.localIsDeclared = typeof response.data.isDeclared === 'boolean' ? response.data.isDeclared : true;
        this.statusId = Number(response.data.statusId ?? this.statusId);
        this.policyNumber = response.data.policyNumber || this.policyNumber;
        this.$emit('order-updated', { orderId: newOrderId });
        this.$router.replace({
          name: 'miniKaskoOrder',
          params: { orderId: newOrderId },
        });
        this.successMessage = response.data.policyNumber
          ? `Поліс заявлено. Номер: ${response.data.policyNumber}`
          : 'Поліс заявлено.';
        await this.fetchOrder();
      } catch (error) {
        this.applyBackendValidationErrors(error);
        this.errorMessage = this.resolveRequestErrorMessage(error, 'Не вдалося заявити поліс.');

        if (!this.focusFirstBackendValidationError()) {
          this.focusErrorAlert();
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    displayValue(value) {
      return value === null || value === undefined || value === '' ? 'Не вказано' : String(value);
    },
    formatDisplayDate(value) {
      if (!value) {
        return 'Не вказано';
      }

      const parsed = moment(value, ['YYYY-MM-DD', 'DD.MM.YYYY'], true);

      return parsed.isValid() ? parsed.format('DD.MM.YYYY') : 'Не вказано';
    },
  },
};
</script>

<style lang="scss" scoped>
.mini-kasko-order {
  &__alert {
    padding: 12px 14px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    line-height: 20px;

    &--error {
      background: #fef2f2;
      color: #b91c1c;
      border: 1px solid #fecaca;
    }

    &--success {
      background: #f0fdf4;
      color: #166534;
      border: 1px solid #bbf7d0;
    }

    &--warning {
      background: #fffbeb;
      color: #92400e;
      border: 1px solid #fde68a;
    }
  }

  &__card {
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06), 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &--wide {
      grid-column: span 2;
    }
  }

  &__group-title {
    font-size: 18px;
    line-height: 24px;
    font-weight: 600;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: span 2;
    }

    span {
      font-size: 13px;
      line-height: 18px;
      color: #52525b;
    }

    input,
    select {
      width: 100%;
      min-height: 44px;
      padding: 10px 12px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      background: #fff;
      outline: none;
      transition: border-color 0.2s ease, box-shadow 0.2s ease;

      &:focus {
        border-color: #fb923c;
        box-shadow: 0 0 0 3px rgba(251, 146, 60, 0.15);
      }

      &[readonly],
      &:disabled {
        background: #f8fafc;
        color: #52525b;
        cursor: default;
      }
    }
  }

  &__field-error {
    color: #dc2626;
    font-size: 12px;
    line-height: 16px;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
  }

  &__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 190px;
    min-height: 48px;
    padding: 0 18px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--secondary {
      background: #fff;
      color: #0a77ff;
      border: 1px solid #cbd5e1;
    }

    &--primary {
      background: #fb923c;
      color: #fff;
      border: 0;
    }
  }

  @media (max-width: 960px) {
    &__grid {
      grid-template-columns: 1fr;
    }

    &__group--wide,
    &__field--wide {
      grid-column: span 1;
    }
  }

  @media (max-width: 640px) {
    &__card {
      padding: 16px;
    }

    &__actions {
      flex-direction: column;
      align-items: stretch;
    }

    &__action {
      width: 100%;
    }
  }
}
</style>
